import { api } from "./api";
import { getProduto } from "config/produto-env";
import {
  ActionLogActionId,
  EtapaVotacao,
  MetodoLogin,
  RegistrarElogioPayload,
  RegistrarFavoritoPayload,
  RegistrarCompartilhamentoPayload
} from "types/iniciativa-action";

const dispatchActionLogCompletedEvent = (actionId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('action-log:completed', {
      detail: {
        actionId,
        timestamp: Date.now()
      }
    })
  );
};

/**
 * Action IDs do Funifier para cada tipo de ação
 *
 * Estes IDs devem estar configurados no painel do Funifier com:
 * - Pontos a serem creditados
 * - Cooldowns (se aplicável)
 * - Vinculação a desafios e conquistas
 */
const ACTION_IDS: Record<string, ActionLogActionId> = {
  ELOGIAR: 'elogiar',
  FAVORITAR: 'favoritar',
  COMPARTILHAR: 'compartilhar',
  LOGIN: 'login',
  ONBOARDING: 'onboarding',
  TIMELINE: 'timeline',
  INICIATIVAS: 'iniciativas',
  ENVIAR_VIDEO: 'enviar_video',
  CONFIRMAR: 'confirmar',
  ACOMPANHAR: 'acompanhar',
  VOTAR: 'votar'
};

/**
 * Registra uma ação no /action/log do Funifier.
 *
 * O `userId` é o _id do player logado (o mesmo do token): o Funifier recusa
 * o registro em nome de outro player e ignora campos que não conhece.
 */
const registrarAcao = async (
  playerId: string,
  actionId: ActionLogActionId,
  attributes: Record<string, unknown> = {}
): Promise<any> => {
  const { data } = await api.post("/action/log", {
    actionId,
    userId: playerId,
    attributes,
  });
  dispatchActionLogCompletedEvent(actionId);
  return data;
};

const chaveDaSessao = (playerId: string, actionId: string, chave?: string) =>
  ['action_log', playerId, actionId, chave].filter(Boolean).join(':');

/**
 * Registra a ação só na primeira vez da sessão do navegador (por player e,
 * se informada, por `chave`). Usada nas ações de navegação, que disparam a
 * cada clique ou visita e só contam uma vez nos desafios.
 *
 * A marca é gravada antes do envio para segurar disparos em sequência
 * (ex.: hover na linha do tempo) e desfeita se o envio falhar.
 */
const registrarAcaoUmaVezPorSessao = async (
  playerId: string,
  actionId: ActionLogActionId,
  attributes: Record<string, unknown> = {},
  chave?: string
): Promise<void> => {
  const marca = chaveDaSessao(playerId, actionId, chave);
  try {
    if (window.sessionStorage.getItem(marca)) return;
    window.sessionStorage.setItem(marca, '1');
  } catch {
    // sessionStorage indisponível (modo privado restrito): registra sempre.
  }

  try {
    await registrarAcao(playerId, actionId, attributes);
  } catch (error) {
    try {
      window.sessionStorage.removeItem(marca);
    } catch {
      /* ignora */
    }
    throw error;
  }
};

/**
 * Registra um elogio em uma iniciativa usando o sistema de action_log do Funifier
 *
 * Envia para /action/log que:
 * - Registra a ação no histórico do Funifier
 * - Dispara gamificação: pontos, badges, desafios
 * - Armazena os dados no campo 'extra' para consultas futuras
 *
 * O /action/log usa a autenticação padrão do apiClient (Bearer Token do usuário logado),
 * não é necessário enviar Basic Auth pois não é uma ação de sistema/admin.
 *
 * @param playerId - _id do player logado
 * @param payload - Dados do elogio
 */
export const registrarElogio = async (
  playerId: string,
  payload: RegistrarElogioPayload
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.ELOGIAR, {
    id_iniciativa: payload.id_iniciativa,
    elogio: payload.elogio
  });
};

/**
 * Registra ou remove um favorito de uma iniciativa usando o sistema de action_log do Funifier
 *
 * Sempre registra a ação no /action/log com o campo 'ativo' nos attributes:
 * - ativo=true: favoritar (contabiliza nos favoritos)
 * - ativo=false: desfavoritar (não contabiliza nos favoritos)
 *
 * A contabilização de favoritos deve considerar apenas registros com ativo=true.
 *
 * @param playerId - _id do player logado
 * @param payload - Dados do favorito
 */
export const registrarFavorito = async (
  playerId: string,
  payload: RegistrarFavoritoPayload
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.FAVORITAR, {
    id_iniciativa: payload.id_iniciativa,
    ativo: payload.ativo
  });
};

/**
 * Registra um compartilhamento de uma iniciativa usando o sistema de action_log do Funifier
 *
 * @param playerId - _id do player logado
 * @param payload - Dados do compartilhamento
 */
export const registrarCompartilhamento = async (
  playerId: string,
  payload: RegistrarCompartilhamentoPayload
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.COMPARTILHAR, {
    id_iniciativa: payload.id_iniciativa
  });
};

export const registrarOnboarding = async (playerId: string): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.ONBOARDING);
};

/** Uma vez por sessão: a linha do tempo dispara a cada hover/clique. */
export const registrarTimeline = async (playerId: string): Promise<void> => {
  return registrarAcaoUmaVezPorSessao(playerId, ACTION_IDS.TIMELINE);
};

/** Uma vez por sessão para cada iniciativa aberta. */
export const registrarIniciativas = async (
  playerId: string,
  idIniciativa: string
): Promise<void> => {
  return registrarAcaoUmaVezPorSessao(
    playerId,
    ACTION_IDS.INICIATIVAS,
    { id_iniciativa: idIniciativa },
    idIniciativa
  );
};

/**
 * Login concluído no front. Simulação e Teams são registrados pelo módulo de
 * autenticação do Funifier; o botão de simulação do Studio grava `login_simulacao`.
 */
export const registrarLogin = async (
  playerId: string,
  metodo: MetodoLogin
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.LOGIN, {
    metodo,
    produto: getProduto(),
  });
};

export const registrarEnviarVideo = async (
  playerId: string,
  idIniciativa: string
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.ENVIAR_VIDEO, { id_iniciativa: idIniciativa });
};

export const registrarConfirmar = async (
  playerId: string,
  idIniciativa: string
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.CONFIRMAR, { id_iniciativa: idIniciativa });
};

/** Uma vez por sessão: a listagem registra a cada visita. */
export const registrarAcompanhar = async (playerId: string): Promise<void> => {
  return registrarAcaoUmaVezPorSessao(playerId, ACTION_IDS.ACOMPANHAR);
};

export const registrarVotar = async (
  playerId: string,
  etapa: EtapaVotacao,
  extras: { preliminar?: boolean } = {}
): Promise<any> => {
  return registrarAcao(playerId, ACTION_IDS.VOTAR, { etapa, ...extras });
};
